/**
 * The dataset as the atlas sees it: the client delivery rolled up through
 * @atlas/shared, plus the display vocabulary (level colours, names, formatters).
 *
 * The arithmetic itself lives in shared/ so the editorial service computes the
 * same numbers from the same code. Pure: no DOM, no three.js.
 */
import { categoriesByDimension } from '@atlas/shared';

/**
 * The published snapshot, fetched rather than bundled: it is an artefact the
 * editorial service will produce, not part of the application. It carries
 * aggregates only — indicator-level assessments never leave the source dataset.
 *
 * Held exactly as delivered. The names in it are attacker-supplied and every
 * screen renders through innerHTML, but they are also keys, so escaping happens
 * where markup is built — see untrusted.js.
 */
const snapshot = await fetch('data/atlas-v1.json')
  .then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  })
  .catch((err) => {
    // the module graph dies here, so the loader would otherwise sit on its first
    // stage forever with no explanation
    const line = document.getElementById('lst');
    if (line) line.textContent = 'Dataset unavailable — ' + err.message;
    throw err;
  });

export const DIMS = snapshot.dims;
export const INDS = snapshot.indicators;
export const COVER = snapshot.cover;          // per dimension: [collected, total] categories / indicators
export const META = snapshot.meta;

/* capability maturity levels — NOT ranks */
export const TIER_COLORS = ['#3A6190', '#4F86B5', '#74AFD6', '#E3C284'];
export const TIER_RGB = TIER_COLORS.map(h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]);
export const TIER_LABEL = ['0 – 1', '1 – 2', '2 – 3', '3 – 4'];   // shown only in Methodology
export const TIER_NAME = ['Emerging', 'Developing', 'Established', 'Advanced'];
export const tierOf = v => v >= 3 ? 3 : v >= 2 ? 2 : v >= 1 ? 1 : 0;

export const catsByDim = categoriesByDimension(INDS, DIMS);

export const hasDim = d => catsByDim[d].length > 0;   // false while a dimension is still being collected
export const DIMS_ACTIVE = DIMS.filter(hasDim);

export const C = snapshot.entities;           // already rolled up by the publisher
C.sort((a, b) => b.overall - a.overall).forEach((c, i) => c.rank = i + 1);

export const byName = Object.fromEntries(C.map(c => [c.n, c]));
export const byIso = Object.fromEntries(C.map(c => [c.iso, c]));
export const byId = {};
C.forEach(c => { if (c.id) byId[c.id] = c });

export const REGIONS = [...new Set(C.map(c => c.r))].sort();
export const EU_MEMBERS = ['Austria', 'Belgium', 'Bulgaria', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden'];
export const REGION_VIEW = {
  'Europe': [54, 15], 'Asia': [34, 95], 'MENA': [27, 18], 'North America': [45, -100],
  'South America': [-15, -60], 'Sub-Saharan Africa': [2, 20], 'Oceania': [-28, 145], 'RCA': [48, 68],
};

export const fmt = v => v.toFixed(2);
export const fmtv = v => v == null ? '—' : v.toFixed(2);            // em dash where nothing was collected
export const tierc = v => v == null ? 'var(--hair)' : TIER_COLORS[tierOf(v)];
export const colorOf = c => TIER_COLORS[tierOf(c.overall)];

export const CATS_ALL = [];
DIMS.forEach(d => catsByDim[d].forEach(cc => CATS_ALL.push({ d, c: cc })));
